import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Grid, Stack, Typography,
  Avatar, Tooltip, IconButton, Chip, Skeleton, CircularProgress,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { alpha } from '@mui/material/styles';

// Icons
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import NightsStayRoundedIcon from '@mui/icons-material/NightsStayRounded';
import AddCardRoundedIcon from '@mui/icons-material/AddCardRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import DirectionsBusRoundedIcon from '@mui/icons-material/DirectionsBusRounded';
import MessageRoundedIcon from '@mui/icons-material/MessageRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';

import PageContainer from '../common/PageContainer';
import EmptyState from '../common/EmptyState';
import axiosInstance from '../../api/axiosConfig';
import { getStoredRoles, hasAllowedRole } from '../../utils/authSession';
import { ROLES } from '../../roles';

const CACHE_PREFIX = 'skhool_dashboard_v6_';
const CACHE_TTL_MS = 1000 * 60 * 5;

const PALETTE = {
  primary: '#059669',
  success: '#10B981',
  warning: '#D97706',
  danger: '#DC2626',
  info: '#0284C7',
  purple: '#6366F1',
  dark: '#0F172A',
};

const getQuickActions = (palette) => [
  { title: 'Students', icon: <GroupRoundedIcon fontSize="small" />, path: '/students/list', color: palette.primary, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN, ROLES.TEACHER, ROLES.LIBRARIAN, ROLES.STAFF, ROLES.COACH] },
  { title: 'Employees', icon: <WorkRoundedIcon fontSize="small" />, path: '/employees', color: palette.info, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN] },
  { title: 'Add Student', icon: <PersonAddRoundedIcon fontSize="small" />, path: '/student/add', color: palette.success, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN] },
  { title: 'Add Staff', icon: <BadgeRoundedIcon fontSize="small" />, path: '/teacher/add', color: palette.warning, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN] },
  { title: 'Students Attend', icon: <HowToRegRoundedIcon fontSize="small" />, path: '/attendance?view=students', color: palette.primary, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN, ROLES.TEACHER, ROLES.EXAMS_ADMIN] },
  { title: 'Staff Attend', icon: <FactCheckRoundedIcon fontSize="small" />, path: '/attendance?view=employees', color: palette.info, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN] },
  { title: 'Fees', icon: <ReceiptLongRoundedIcon fontSize="small" />, path: '/fee-management', color: palette.success, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN, ROLES.FINANCE_ADMIN] },
  { title: 'Expenses', icon: <AddCardRoundedIcon fontSize="small" />, path: '/expenses', color: palette.danger, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN, ROLES.FINANCE_ADMIN] },
  { title: 'Exams', icon: <SchoolRoundedIcon fontSize="small" />, path: '/academic-results', color: palette.purple, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN, ROLES.EXAMS_ADMIN, ROLES.TEACHER, ROLES.STUDENT] },
  { title: 'Events', icon: <EventAvailableRoundedIcon fontSize="small" />, path: '/events?tab=events', color: palette.warning, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN, ROLES.TEACHER, ROLES.STAFF, ROLES.COACH] },
  { title: 'Transport', icon: <DirectionsBusRoundedIcon fontSize="small" />, path: '/transportation/buses', color: palette.primary, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN, ROLES.TEACHER, ROLES.STAFF, ROLES.COACH] },
  { title: 'Messages', icon: <MessageRoundedIcon fontSize="small" />, path: '/send-notifications', color: palette.success, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN, ROLES.TEACHER, ROLES.STAFF, ROLES.COACH] },
  { title: 'Reports', icon: <SummarizeRoundedIcon fontSize="small" />, path: '/reports', color: palette.dark, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.APP_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.ADMIN, ROLES.TEACHER, ROLES.STAFF, ROLES.COACH] },
];

const fCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val || 0);

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')} ${d.toLocaleString('en-US', { month: 'short' })}`;
};

const formatTaskMoment = (date, time) => {
  if (!date) return 'Date not set';
  const rawValue = time ? `${date}T${time}` : `${date}T09:00`;
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) return `${date}${time ? ` ${time}` : ''}`;
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ROLE_DISPLAY_NAME = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.APP_ADMIN]: 'App Admin',
  [ROLES.SCHOOL_ADMIN]: 'School Admin',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.FINANCE_ADMIN]: 'Finance Admin',
  [ROLES.EXAMS_ADMIN]: 'Exams Admin',
  [ROLES.TEACHER]: 'Teacher',
  [ROLES.LIBRARIAN]: 'Librarian',
  [ROLES.STAFF]: 'Staff',
  [ROLES.COACH]: 'Coach',
  [ROLES.STUDENT]: 'Student',
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Good Morning', icon: <WbSunnyRoundedIcon sx={{ color: PALETTE.warning }} /> };
  if (hour < 18) return { text: 'Good Afternoon', icon: <WbSunnyRoundedIcon sx={{ color: PALETTE.warning }} /> };
  return { text: 'Good Evening', icon: <NightsStayRoundedIcon sx={{ color: PALETTE.purple }} /> };
};

const DashboardSkeleton = () => (
  <PageContainer maxWidth={false} sx={{ pb: 6 }}>
    <Box sx={{ p: 2 }}>
      <Skeleton variant="text" width="40%" height={32} />
      <Skeleton variant="text" width="60%" height={24} sx={{ mb: 3 }} />
      <Grid container spacing={2}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Grid item xs={12} sm={3} key={i}>
            <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 1.5 }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  </PageContainer>
);

const DashboardV3 = () => {
  const navigate = useNavigate();
  const sessionRoles = useMemo(() => getStoredRoles(), []);

  const isPlatformAdmin = useMemo(() => hasAllowedRole(['SYSTEM_ADMIN', 'SUPER_ADMIN', 'APP_ADMIN'], sessionRoles), [sessionRoles]);
  const isSchoolScopedUser = useMemo(
    () =>
      !isPlatformAdmin &&
      hasAllowedRole(
        ['SCHOOL_ADMIN', 'ADMIN', 'FINANCE_ADMIN', 'EXAMS_ADMIN', 'TEACHER', 'LIBRARIAN', 'STAFF', 'COACH', 'STUDENT'],
        sessionRoles
      ),
    [isPlatformAdmin, sessionRoles]
  );
  const canViewAttendance = useMemo(
    () => hasAllowedRole(['SCHOOL_ADMIN', 'ADMIN', 'EXAMS_ADMIN', 'TEACHER'], sessionRoles),
    [sessionRoles]
  );
  const roleLabel = useMemo(
    () => sessionRoles.map((role) => ROLE_DISPLAY_NAME[role]).find(Boolean) || 'User',
    [sessionRoles]
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const [platformData, setPlatformData] = useState(null);
  const [schoolData, setSchoolData] = useState(null);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [websiteRequestSummary, setWebsiteRequestSummary] = useState({});
  const [websiteRequests, setWebsiteRequests] = useState([]);

  const schoolId = useMemo(() => String(localStorage.getItem('schoolId') || '').trim(), []);
  const greeting = useMemo(() => getGreeting(), []);
  const quickActions = useMemo(
    () => getQuickActions(PALETTE).filter((action) => hasAllowedRole(action.allowedRoles, sessionRoles)),
    [sessionRoles]
  );

  const fetchSchoolData = useCallback(async (force = false) => {
    if (!isSchoolScopedUser || !schoolId) return;
    if (!force) {
      try {
        const cached = JSON.parse(localStorage.getItem(`${CACHE_PREFIX}${schoolId}`));
        if (cached && Date.now() - cached.savedAt < CACHE_TTL_MS) {
          setSchoolData(cached.data);
          setLastSyncedAt(cached.savedAt);
          setLoading(false);
          return;
        }
      } catch (e) {}
    }
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/api/dashboard/all', { headers: { schoolId } });
      setSchoolData(response?.data?.data);
      setLastSyncedAt(Date.now());
      localStorage.setItem(`${CACHE_PREFIX}${schoolId}`, JSON.stringify({ savedAt: Date.now(), data: response?.data?.data }));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [isSchoolScopedUser, schoolId]);

  const fetchWebsiteRequests = useCallback(async () => {
    if (!isSchoolScopedUser || !schoolId) return;
    try {
      const [summaryResp, requestsResp] = await Promise.all([
        axiosInstance.get('/api/website-requests/summary', { headers: { schoolId } }),
        axiosInstance.get('/api/website-requests', { headers: { schoolId } }),
      ]);
      setWebsiteRequestSummary(summaryResp?.data || {});
      setWebsiteRequests(Array.isArray(requestsResp?.data) ? requestsResp.data.slice(0, 5) : []);
    } catch (_error) {
      setWebsiteRequestSummary({});
      setWebsiteRequests([]);
    }
  }, [isSchoolScopedUser, schoolId]);

  const fetchScheduledTasks = useCallback(async () => {
    if (!isSchoolScopedUser || !schoolId) return;
    try {
      const response = await axiosInstance.get('/api/task-schedules', { headers: { schoolId } });
      setScheduledTasks(Array.isArray(response.data) ? response.data : []);
    } catch (_error) {
      setScheduledTasks([]);
    }
  }, [isSchoolScopedUser, schoolId]);

  const fetchPlatformData = useCallback(async () => {
    if (!isPlatformAdmin) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/dashboard/platform-overview');
      setPlatformData(response?.data?.data);
      setLastSyncedAt(Date.now());
    } catch (err) {
      setError('Failed to load platform overview.');
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    if (isPlatformAdmin) fetchPlatformData();
    else if (isSchoolScopedUser) fetchSchoolData();
    else setLoading(false);
  }, [isPlatformAdmin, isSchoolScopedUser, fetchPlatformData, fetchSchoolData]);

  useEffect(() => {
    fetchWebsiteRequests();
  }, [fetchWebsiteRequests]);

  useEffect(() => {
    fetchScheduledTasks();
  }, [fetchScheduledTasks]);

  if (!isPlatformAdmin && !isSchoolScopedUser) return <PageContainer><EmptyState title="Access Denied" /></PageContainer>;

  const hasData = Boolean(schoolData || platformData);

  if (error) {
    return (
      <PageContainer maxWidth={false}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{error}</Alert>
        <Button onClick={() => fetchSchoolData(true)} variant="contained">Retry</Button>
      </PageContainer>
    );
  }

  if (loading && !hasData) {
    return <DashboardSkeleton />;
  }

  if (isSchoolScopedUser && schoolData) {
    const { totals, feeAndExpenses, analytics, events = [], holidays = [] } = schoolData;
    const todayIso = new Date().toISOString().split('T')[0];
    const activeTasks = scheduledTasks
      .filter((task) => String(task.status || '').toUpperCase() !== 'DONE')
      .sort((a, b) => `${a.taskDate || ''} ${a.taskTime || ''}`.localeCompare(`${b.taskDate || ''} ${b.taskTime || ''}`));

    const upcomingMoments = [
      ...events.map((event) => ({ id: `event-${event.eventId || event.title}`, title: event.title, date: event.startDate, tag: 'Event', color: PALETTE.info })),
      ...holidays.map((holiday) => ({ id: `holiday-${holiday.holidayId || holiday.name}`, title: holiday.name, date: holiday.start_date, tag: 'Holiday', color: PALETTE.warning })),
      ...activeTasks.map((task) => ({ id: `task-${task.id}`, title: task.title, date: task.taskDate, tag: 'Task', color: PALETTE.primary })),
    ]
      .filter((item) => item.date && item.date >= todayIso)
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))
      .slice(0, 5);

    const monthNet = (feeAndExpenses?.thisMonthCollection || 0) - (feeAndExpenses?.thisMonthExpenses || 0);

    return (
      <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
        {/* HEADER BAR */}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 3 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              {greeting.icon}
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A' }}>
                {greeting.text}, {roleLabel}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
              School Operational Overview & Daily Summary
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: { xs: 1.5, sm: 0 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Synced: {lastSyncedAt ? new Date(lastSyncedAt).toLocaleTimeString() : 'Now'}
            </Typography>
            <Tooltip title="Refresh Dashboard">
              <IconButton onClick={() => fetchSchoolData(true)} size="small" sx={{ border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
                <RefreshRoundedIcon fontSize="small" sx={{ color: PALETTE.primary }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* METRICS STAT BAR (Clean 1px grid without box-in-box cards) */}
        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3.5, borderColor: '#E2E8F0', overflow: 'hidden' }}>
          <Grid container divider={<Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', bgcolor: '#E2E8F0' }} />}>
            <Grid item xs={12} sm={6} md={3} sx={{ p: 2.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total Students
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: PALETTE.primary, my: 0.5 }}>
                {totals?.students || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totals?.boys || 0} Boys • {totals?.girls || 0} Girls
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ p: 2.5, borderLeft: { xs: 'none', sm: '1px solid #E2E8F0' } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total Staff
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: PALETTE.info, my: 0.5 }}>
                {totals?.employees || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totals?.teachers || 0} Teachers • Ratio {analytics?.studentTeacherRatio || 0}:1
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ p: 2.5, borderTop: { xs: '1px solid #E2E8F0', md: 'none' } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Today Attendance
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: PALETTE.success, my: 0.5 }}>
                {analytics?.studentAttendanceRate || 0}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Staff Attendance {analytics?.employeeAttendanceRate || 0}%
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ p: 2.5, borderTop: { xs: '1px solid #E2E8F0', md: 'none' }, borderLeft: { xs: 'none', sm: '1px solid #E2E8F0' } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Net Monthly Cashflow
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: monthNet < 0 ? PALETTE.danger : PALETTE.primary, my: 0.5 }}>
                {fCurrency(monthNet)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pending: {fCurrency(feeAndExpenses?.totalPendingFee)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* QUICK SHORTCUTS TOOLBAR */}
        {quickActions.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', mb: 1.5 }}>
              Quick Actions
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
              {quickActions.map((action) => (
                <Button
                  key={action.title}
                  variant="outlined"
                  size="small"
                  startIcon={action.icon}
                  onClick={() => navigate(action.path)}
                  sx={{
                    borderRadius: 1.5,
                    borderColor: '#E2E8F0',
                    color: '#0F172A',
                    fontWeight: 700,
                    textTransform: 'none',
                    bgcolor: '#FFFFFF',
                    px: 1.8,
                    py: 0.8,
                    '&:hover': {
                      borderColor: action.color,
                      bgcolor: alpha(action.color, 0.04),
                    },
                  }}
                >
                  {action.title}
                </Button>
              ))}
            </Stack>
          </Box>
        )}

        {/* MAIN 2-COLUMN UNIFIED DESK (Clean Tables & Minimal Lists) */}
        <Grid container spacing={3}>
          {/* LEFT COLUMN: Tasks & Requests */}
          <Grid item xs={12} lg={7}>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mb: 3, borderColor: '#E2E8F0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                    Scheduled Tasks & Action Items
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active operational items requiring follow-up
                  </Typography>
                </Box>
                <Button size="small" onClick={() => navigate('/schedule-tasks')} sx={{ fontWeight: 700, textTransform: 'none' }}>
                  Manage Tasks
                </Button>
              </Stack>

              {activeTasks.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Task Title</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Scheduled Time</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeTasks.slice(0, 5).map((task) => (
                        <TableRow key={task.id}>
                          <TableCell sx={{ fontWeight: 600 }}>{task.title}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{formatTaskMoment(task.taskDate, task.taskTime)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={task.status || 'PENDING'} color={task.status === 'DONE' ? 'success' : 'warning'} sx={{ borderRadius: 1, fontWeight: 700, fontSize: '0.7rem' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No pending scheduled tasks. All caught up!
                </Typography>
              )}
            </Paper>

            {/* WEBSITE LEADS TABLE */}
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: '#E2E8F0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                    Website Enquiries & Leads
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    New requests from the school website
                  </Typography>
                </Box>
                <Button size="small" onClick={() => navigate('/mobile-web-applications')} sx={{ fontWeight: 700, textTransform: 'none' }}>
                  View All
                </Button>
              </Stack>

              {websiteRequests.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Contact Info</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {websiteRequests.map((req) => (
                        <TableRow key={req.requestId}>
                          <TableCell sx={{ fontWeight: 600 }}>{req.parentName || req.name || 'Enquiry'}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{req.email || req.phone || '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={String(req.requestType || 'GENERAL').replace('_', ' ')} sx={{ borderRadius: 1, fontWeight: 700, fontSize: '0.7rem' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No new website enquiries.
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* RIGHT COLUMN: Financial & Timeline Overview */}
          <Grid item xs={12} lg={5}>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mb: 3, borderColor: '#E2E8F0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                  Financial Breakdown
                </Typography>
                <Button size="small" onClick={() => navigate('/fee-management')} sx={{ fontWeight: 700, textTransform: 'none' }}>
                  Fee Portal
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: '1px solid #F1F5F9' }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>Total Collected Fees</Typography>
                  <Typography variant="body1" fontWeight={800} color={PALETTE.success}>{fCurrency(feeAndExpenses?.totalCollectedFee)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: '1px solid #F1F5F9' }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>Outstanding Pending Fees</Typography>
                  <Typography variant="body1" fontWeight={800} color={PALETTE.warning}>{fCurrency(feeAndExpenses?.totalPendingFee)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: '1px solid #F1F5F9' }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>This Month Expenses</Typography>
                  <Typography variant="body1" fontWeight={800} color={PALETTE.danger}>{fCurrency(feeAndExpenses?.thisMonthExpenses)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>Realization Rate</Typography>
                  <Typography variant="body1" fontWeight={800}>{analytics?.feeRealizationRate || 0}%</Typography>
                </Stack>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: '#E2E8F0' }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', mb: 2 }}>
                Upcoming Timeline
              </Typography>
              {upcomingMoments.length > 0 ? (
                <Stack spacing={1.5}>
                  {upcomingMoments.map((item) => (
                    <Stack key={item.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: '1px solid #F1F5F9' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.tag}</Typography>
                      </Box>
                      <Chip size="small" label={formatDateShort(item.date)} sx={{ borderRadius: 1, fontWeight: 700, fontSize: '0.7rem' }} />
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No upcoming timeline items.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // PLATFORM ADMIN VIEW
  if (isPlatformAdmin && platformData) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Platform Overview</Typography>
            <Typography variant="body2" color="text.secondary">Global Network Metrics</Typography>
          </Box>
          <Button startIcon={<RefreshRoundedIcon />} onClick={fetchPlatformData} variant="outlined" size="small" sx={{ borderRadius: 1.5 }}>
            Refresh
          </Button>
        </Stack>
      </Box>
    );
  }

  return null;
};

export default DashboardV3;
